import { useCallback, useEffect, useRef, useState } from "react";
import { groupByLocator } from "./excel";
import { taskTypeLabel } from "./hw-check";
import type { TaskDetail, TaskLine } from "./hw-check";
import { downloadSelection, getErrorMessage, loadJson, readApiError } from "./lib";
import { CameraScanProvider, ScanField } from "./scan";

/**
 * SN-Bom mismatch entry, laid out the same way the seal check is: rows grouped
 * by locator, each row keeping its own unsaved answer so a half-done task still
 * shows what is already in. A finished task leaves the app as an Excel workbook
 * instead of a PDF — there is no sheet to print and sign here.
 */

type SnBomAnswer = { physicalBom: string; physicalSn: string; sealResult: string; remark: string };

type PendingMap = Record<number, SnBomAnswer>;

function savedAnswer(line: TaskLine): SnBomAnswer {
  return {
    physicalBom: line.physicalBom,
    physicalSn: line.physicalSn,
    sealResult: line.sealResult,
    remark: line.remark,
  };
}

function answerOf(line: TaskLine, pending: PendingMap) {
  return pending[line.id] ?? savedAnswer(line);
}

function isLineReady(line: TaskLine, pending: PendingMap) {
  const answer = answerOf(line, pending);
  return (
    Boolean(answer.physicalBom) &&
    Boolean(answer.physicalSn) &&
    (answer.sealResult === "pass" || answer.sealResult === "fail")
  );
}

function hasPendingChange(line: TaskLine, pending: PendingMap) {
  const change = pending[line.id];
  if (!change) return false;
  const saved = savedAnswer(line);
  return (
    change.physicalBom !== saved.physicalBom ||
    change.physicalSn !== saved.physicalSn ||
    change.sealResult !== saved.sealResult ||
    change.remark !== saved.remark
  );
}

function CheckMark() {
  return (
    <svg className="ready-check" aria-hidden="true" viewBox="0 0 24 24">
      <path d="m5 12 4 4L19 6" />
    </svg>
  );
}

export default function HwCheckTaskSnBom({
  taskId,
  onClose,
  onChanged,
}: {
  taskId: number;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [detail, setDetail] = useState<TaskDetail | null>(null);
  const [pending, setPending] = useState<PendingMap>({});
  const [closedGroups, setClosedGroups] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const didCollapse = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);

  const loadDetail = useCallback(async () => {
    const data = await loadJson<{ task: TaskDetail }>(
      `/api/hw-check-tasks?id=${taskId}`,
      "A task betöltése sikertelen.",
    );
    setDetail(data.task);
    return data.task;
  }, [taskId]);

  useEffect(() => {
    let isCurrent = true;

    void (async () => {
      try {
        const task = await loadDetail();
        if (isCurrent && !didCollapse.current) {
          didCollapse.current = true;
          setClosedGroups(
            groupByLocator(task.lines)
              .filter((group) => group.items.every((line) => isLineReady(line, {})))
              .map((group) => group.locator),
          );
        }
      } catch (loadError) {
        if (isCurrent) {
          setError(getErrorMessage(loadError, "A task betöltése sikertelen."));
        }
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    })();

    return () => {
      isCurrent = false;
    };
  }, [loadDetail]);

  const answer = (line: TaskLine, change: Partial<SnBomAnswer>) => {
    setError("");
    setPending((current) => ({
      ...current,
      [line.id]: { ...answerOf(line, current), ...change },
    }));
  };

  // One shared camera handler for every row's two scan fields — the field
  // name carries both which line and which of the two columns it answers,
  // e.g. "physicalBom-482".
  const lookupById = new Map((detail?.lines ?? []).map((line) => [line.id, line]));
  const onCameraValue = useCallback(
    (field: string, value: string) => {
      const separator = field.lastIndexOf("-");
      const key = field.slice(0, separator);
      const lineId = Number(field.slice(separator + 1));
      const line = lookupById.get(lineId);
      if (!line || (key !== "physicalBom" && key !== "physicalSn")) return;
      answer(line, { [key]: value } as Partial<SnBomAnswer>);
    },
    [detail],
  );

  const saveAnswers = async () => {
    if (!detail || isSaving) return;

    const changed = detail.lines.filter((line) => hasPendingChange(line, pending));
    if (changed.length === 0) {
      setMessage("");
      setError("Nincs mentendő válasz.");
      return;
    }

    setIsSaving(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch("/api/hw-check-task-sn-bom", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          taskId: detail.id,
          lines: changed.map((line) => ({ id: line.id, ...answerOf(line, pending) })),
        }),
      });
      if (!response.ok) {
        throw new Error(await readApiError(response, "A válaszok mentése sikertelen."));
      }

      const data = (await response.json()) as { task: TaskDetail | null };
      setPending({});
      const task = data.task ?? (await loadDetail());
      setDetail(task);
      setMessage(
        `${changed.length} sor mentve. ${task.completedLines}/${task.lineCount} sor kész${
          task.isComplete ? ", a task teljes — letöltheted Excelben." : "."
        }`,
      );
    } catch (saveError) {
      setError(getErrorMessage(saveError, "Ismeretlen mentési hiba történt."));
      await loadDetail().catch(() => undefined);
    } finally {
      setIsSaving(false);
      onChanged();
    }
  };

  const downloadXlsx = async () => {
    if (!detail) return;

    setIsDownloading(true);
    setError("");
    setMessage("");

    try {
      const fileName = await downloadSelection(
        "/api/hw-check-tasks/sn-bom",
        [detail.id],
        `${detail.taskCode}.xlsx`,
      );
      setMessage(`${fileName} letöltve.`);
    } catch (downloadError) {
      setError(getErrorMessage(downloadError, "Ismeretlen letöltési hiba történt."));
    } finally {
      setIsDownloading(false);
    }
  };

  const toggleGroup = (locator: string, isOpen: boolean) => {
    setClosedGroups((current) =>
      isOpen
        ? current.filter((item) => item !== locator)
        : current.includes(locator)
          ? current
          : [...current, locator],
    );
  };

  if (isLoading) {
    return (
      <section className="saved-panel">
        <div className="record-skeleton" aria-label="Loading task">
          <i />
          <i />
          <i />
        </div>
      </section>
    );
  }

  if (!detail) {
    return (
      <section className="saved-panel">
        {error && <p className="status-message error-message">{error}</p>}
        <div className="empty-state">
          <h3>Task not found</h3>
          <p>It may have been deleted on another device.</p>
        </div>
        <div className="form-actions">
          <button className="modify-button" type="button" onClick={onClose}>
            BACK TO TASKS
          </button>
        </div>
      </section>
    );
  }

  const groups = groupByLocator(detail.lines);
  const readyLines = detail.lines.filter((line) => isLineReady(line, pending)).length;
  const pendingLines = detail.lines.filter((line) => hasPendingChange(line, pending)).length;
  const isComplete = readyLines === detail.lines.length && detail.lines.length > 0;
  const canDownload = isComplete && pendingLines === 0;

  return (
    <CameraScanProvider formRef={formRef} onValue={onCameraValue}>
      <section className="saved-panel task-detail">
        <div className="panel-heading">
          <div>
            <p>{taskTypeLabel(detail.taskType).toUpperCase()}</p>
            <h2>
              {detail.taskCode}
              {canDownload && <CheckMark />}
            </h2>
          </div>
          <span>
            {readyLines}/{detail.lines.length} ROWS READY
          </span>
        </div>

        <div className="task-detail-bar">
          <button className="modify-button" type="button" onClick={onClose}>
            ← TASKS
          </button>
          <span className="task-file-name">{detail.sourceFileName}</span>
          <button
            className="download-button"
            type="button"
            disabled={isDownloading || !canDownload}
            title={
              canDownload
                ? `Download ${detail.taskCode}.xlsx`
                : "Az Excel akkor tölthető le, ha minden sor mentve és megválaszolva."
            }
            onClick={() => void downloadXlsx()}
          >
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path d="M12 4v10m0 0 4-4m-4 4-4-4M5 19h14" />
            </svg>
            {isDownloading ? "BUILDING XLSX…" : "DOWNLOAD XLSX"}
          </button>
        </div>

        <div className="seal-signers">
          {(detail.checkedBy || detail.confirmedBy) && (
            <>
              <div>
                <span>CHECKED BY</span>
                <strong>{detail.checkedBy || "—"}</strong>
              </div>
              <div>
                <span>CONFIRMED BY</span>
                <strong>{detail.confirmedBy || "—"}</strong>
              </div>
            </>
          )}
          <div>
            <span>OK</span>
            <strong>{detail.passCount}</strong>
          </div>
          <div>
            <span>NO</span>
            <strong>{detail.failCount}</strong>
          </div>
        </div>

        {message && <p className="status-message success-message">{message}</p>}
        {error && <p className="status-message error-message">{error}</p>}

        <form ref={formRef} onSubmit={(event) => event.preventDefault()}>
          <div className="locator-groups">
            {groups.map((group) => {
              const groupReady = group.items.filter((line) =>
                isLineReady(line, pending),
              ).length;
              const isGroupComplete = groupReady === group.items.length;

              return (
                <details
                  key={group.locator}
                  className={`locator-group ${isGroupComplete ? "is-complete" : ""}`}
                  open={!closedGroups.includes(group.locator)}
                  onToggle={(event) => toggleGroup(group.locator, event.currentTarget.open)}
                >
                  <summary>
                    <span className="locator-label">LOCATOR</span>
                    <strong className="locator-name">{group.locator}</strong>
                    <span className="locator-progress">
                      {groupReady}/{group.items.length}
                    </span>
                    {isGroupComplete && <CheckMark />}
                  </summary>

                  {group.items.map((line) => {
                    const current = answerOf(line, pending);
                    const lineReady = isLineReady(line, pending);

                    return (
                      <div
                        className={`task-line ${lineReady ? "is-ready" : ""} ${
                          current.sealResult === "fail" ? "is-failed" : ""
                        }`}
                        key={line.id}
                      >
                        <div className="line-cells">
                          <div>
                            <span>SYSTEM BOM</span>
                            <strong>{line.item}</strong>
                          </div>
                          <div>
                            <span>SYSTEM SN</span>
                            <strong>{line.sn}</strong>
                          </div>
                        </div>

                        <div className="line-seal">
                          <ScanField
                            label="PHYSICAL BOM"
                            name={`physicalBom-${line.id}`}
                            value={current.physicalBom}
                            onValue={(_field, value) => answer(line, { physicalBom: value })}
                          />
                          <ScanField
                            label="PHYSICAL SN"
                            name={`physicalSn-${line.id}`}
                            value={current.physicalSn}
                            onValue={(_field, value) => answer(line, { physicalSn: value })}
                          />

                          <div className="field seal-checkboxes">
                            <span>SEAL INTACT</span>
                            <div className="checkbox-pair">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={current.sealResult === "pass"}
                                  onChange={(event) =>
                                    answer(line, {
                                      sealResult: event.target.checked ? "pass" : "",
                                    })
                                  }
                                />
                                OK
                              </label>
                              <label>
                                <input
                                  type="checkbox"
                                  checked={current.sealResult === "fail"}
                                  onChange={(event) =>
                                    answer(line, {
                                      sealResult: event.target.checked ? "fail" : "",
                                    })
                                  }
                                />
                                NO
                              </label>
                            </div>
                          </div>

                          <label className="field field-wide">
                            <span>REMARK</span>
                            <input
                              type="text"
                              name={`remark-${line.id}`}
                              maxLength={400}
                              placeholder="nem kötelező"
                              value={current.remark}
                              onChange={(event) => answer(line, { remark: event.target.value })}
                            />
                          </label>
                        </div>

                        <p className="line-status">
                          {lineReady ? (
                            <>
                              <CheckMark /> {current.sealResult === "pass" ? "OK" : "NO"}
                            </>
                          ) : (
                            "NOT CHECKED"
                          )}
                        </p>
                      </div>
                    );
                  })}
                </details>
              );
            })}
          </div>
        </form>

        <div className="form-actions task-save-bar">
          <span className="pending-count">
            {pendingLines > 0
              ? `${pendingLines} sor mentésre vár`
              : canDownload
                ? "Minden sor ellenőrizve"
                : "Nincs mentendő változás"}
          </span>
          <button
            className="save-button"
            type="button"
            disabled={isSaving || pendingLines === 0}
            onClick={() => void saveAnswers()}
          >
            {isSaving ? "SAVING…" : "SAVE CHECKS"}
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path d="m5 12 4 4L19 6" />
            </svg>
          </button>
        </div>
      </section>
    </CameraScanProvider>
  );
}
